import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  IconChevronLeft, IconBox, IconStar, IconTrash,
  IconBackpack, IconShare, IconArrowRight, IconUsers,
  IconWorld, IconLock, IconLayoutBottombar, IconHome,
} from '@tabler/icons-react'
import { containersApi, type Container } from '../../lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────

type NavItem = {
  to: string
  label: string
  icon?: React.ReactNode
  end?: boolean
  indent?: number
}

type NavSection = { label?: string; items: NavItem[] }

// ── Static navs (no dynamic data needed) ─────────────────────────────────────

const GEAR_NAV: NavSection[] = [
  { items: [
    { to: '/gear', label: 'All Gear', icon: <IconBox size={15} />, end: true, indent: 0 },
    { to: '/gear/favorites', label: 'Favorites', icon: <IconStar size={15} />, indent: 0 },
    { to: '/recently-deleted', label: 'Recently Deleted', icon: <IconTrash size={15} />, indent: 0 },
  ]},
]

const KIT_COLLECTIONS_NAV: NavSection[] = [
  { items: [
    { to: '/kit-collections', label: 'My Collections', icon: <IconBackpack size={15} />, end: true, indent: 0 },
    { to: '/kit-collections/favorites', label: 'Favorites', icon: <IconStar size={15} />, indent: 0 },
    { to: '/kit-collections/shared-with-me', label: 'Shared With Me', icon: <IconShare size={15} />, indent: 0 },
    { to: '/kit-collections/shared-by-me', label: 'Shared By Me', icon: <IconArrowRight size={15} />, indent: 0 },
    { to: '/kit-collections/community', label: 'Community', icon: <IconUsers size={15} />, indent: 0 },
    { to: '/kit-collections/public', label: 'Public', icon: <IconWorld size={15} />, indent: 0 },
  ]},
]

const COMMUNITY_NAV: NavSection[] = [
  { items: [
    { to: '/community', label: 'Browse Gear', icon: <IconWorld size={15} />, end: true, indent: 0 },
    { to: '/community/bags', label: 'Browse Bags', icon: <IconUsers size={15} />, indent: 0 },
  ]},
]

const RECENTLY_DELETED_NAV: NavSection[] = [
  { items: [
    { to: '/recently-deleted', label: 'Recently Deleted', icon: <IconTrash size={15} />, end: true, indent: 0 },
  ]},
]

// ── Build locker nav dynamically from real container data ─────────────────────

function buildLockerNav(lockers: Container[]): NavSection[] {
  if (lockers.length === 0) {
    // Fallback while loading — show top level only
    return [{ items: [
      { to: '/lockers', label: 'My Lockers', icon: <IconLock size={15} />, end: true, indent: 0 },
    ]}]
  }

  const items: NavItem[] = [
    { to: '/lockers', label: 'My Lockers', icon: <IconLock size={15} />, end: true, indent: 0 },
  ]

  for (const locker of lockers) {
    const label = locker.isDefault ? `${locker.name} ★` : locker.name
    items.push({
      to: `/lockers/${locker.id}`,
      label,
      icon: <IconLock size={13} />,
      end: true,
      indent: 1,
    })
    items.push({
      to: `/lockers/${locker.id}/bins`,
      label: 'Gear Bags',
      icon: <IconLayoutBottombar size={13} />,
      end: true,
      indent: 2,
    })
  }

  return [{ items }]
}

// ── Path matching ─────────────────────────────────────────────────────────────

type SectionKey = 'lockers' | 'kit-collections' | 'gear' | 'community' | 'recently-deleted' | null

function getSectionKey(pathname: string): SectionKey {
  if (pathname.startsWith('/lockers')) return 'lockers'
  if (pathname.startsWith('/kit-collections')) return 'kit-collections'
  if (pathname.startsWith('/gear')) return 'gear'
  if (pathname.startsWith('/recently-deleted')) return 'recently-deleted'
  if (pathname.startsWith('/community')) return 'community'
  return null
}

// ── Constants ─────────────────────────────────────────────────────────────────

const INDENT = ['', 'pl-4', 'pl-8']

// ── Persistent trash footer ───────────────────────────────────────────────────

function TrashFooter({ collapsed }: { collapsed?: boolean }) {
  return (
    <div className="px-2 pb-3 border-t border-border-subtle pt-2">
      <NavLink to="/recently-deleted"
        className={({ isActive }) => [
          'flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors text-xs',
          collapsed ? 'justify-center' : '',
          isActive
            ? 'text-brand-orange bg-surface-raised font-medium'
            : 'text-zinc-500 hover:text-zinc-300 hover:bg-surface-raised',
        ].join(' ')}
        title="Recently Deleted">
        <IconTrash size={18} className="shrink-0" />
        {!collapsed && <span>Recently Deleted</span>}
      </NavLink>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

type SideNavProps = { collapsed: boolean; onToggle: () => void }

export function SideNav({ collapsed, onToggle }: SideNavProps) {
  const { pathname } = useLocation()
  const sectionKey = getSectionKey(pathname)

  // Lockers are fetched dynamically — only when in the lockers section
  const [lockers, setLockers] = useState<Container[]>([])

  useEffect(() => {
    if (sectionKey !== 'lockers') return
    containersApi.list({ type: 'locker' })
      .then(res => setLockers(res.data ?? []))
      .catch(() => {})
  }, [sectionKey])

  if (!sectionKey) return (
    <aside className="flex flex-col shrink-0 bg-surface-overlay border-r border-border-default w-10 overflow-hidden">
      <div className="flex-1" />
      <TrashFooter collapsed />
    </aside>
  )

  let sections: NavSection[]
  switch (sectionKey) {
    case 'lockers':       sections = buildLockerNav(lockers); break
    case 'kit-collections': sections = KIT_COLLECTIONS_NAV; break
    case 'gear':          sections = GEAR_NAV; break
    case 'community':     sections = COMMUNITY_NAV; break
    case 'recently-deleted': sections = RECENTLY_DELETED_NAV; break
  }

  return (
    <aside className={[
      'flex flex-col shrink-0 bg-surface-overlay border-r border-border-default',
      'transition-all duration-200 overflow-hidden',
      collapsed ? 'w-10' : 'w-52',
    ].join(' ')}>
      <div className="flex justify-end px-2 pt-2 pb-1">
        <button onClick={onToggle}
          className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
          <IconChevronLeft size={13}
            className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <nav className="flex flex-col gap-0.5 px-2 pb-4 flex-1 overflow-y-auto">
        {sections.map((section, si) => (
          <div key={si} className="mt-1">
            {!collapsed && section.label && (
              <div className="px-2 pt-2 pb-1 text-xs font-medium uppercase tracking-widest text-zinc-400">
                {section.label}
              </div>
            )}
            {section.items.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) => [
                  'flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors',
                  collapsed ? 'justify-center' : (INDENT[item.indent ?? 0] ?? ''),
                  isActive
                    ? 'text-brand-orange bg-surface-raised font-medium'
                    : 'text-zinc-300 hover:text-white hover:bg-surface-raised',
                  item.indent === 0 ? 'text-sm' : 'text-xs',
                ].join(' ')}
                title={collapsed ? item.label : undefined}>
                {item.icon && <span className="shrink-0 text-inherit">{item.icon}</span>}
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <TrashFooter collapsed={collapsed} />
    </aside>
  )
}
